/**
 * @engine/core - ResourceManager
 *
 * Host-agnostic refcounted asset ownership ledger. Tracks how many loaded levels
 * reference each asset GUID and drives load/dispose through an injected
 * {@link ResourceBackends} adapter (editor and player provide their own).
 *
 * Core does NOT import render2d / audio — the host wires the actual GPU/audio/material
 * load + dispose. This keeps the seam clean and the same refcount semantics on both hosts.
 *
 * Key invariants:
 *  - `acquire(guids)` increments refcounts; the backend `load()` runs only on the 0→1 edge.
 *  - `release(guids)` decrements; the backend `dispose()` runs only on the →0 edge (unless pinned).
 *  - Assets shared by two loaded levels reach refcount 2 → unloading one never frees them.
 *  - `pin()` makes an asset refcount-immune (for `dontDestroyOnLoad` objects).
 *
 * Seamless level swap = `acquire(next.closure)` BEFORE `release(prev.closure)`, so any asset
 * shared between the two levels never hits 0 → never unloaded-then-reloaded → no flash.
 */

export type AssetKind = "texture" | "audio" | "material" | "json" | "binary";

/** Host adapter that performs the actual load/dispose/classification of an asset. */
export interface ResourceBackends {
  /** Idempotent load (host caches are already idempotent). Resolves when ready. */
  load(kind: AssetKind, guid: string): Promise<void> | void;
  /** Free the underlying GPU/audio/material resource for `guid`. */
  dispose(kind: AssetKind, guid: string): void;
  /** Classify a GUID into an asset kind (e.g. from the manifest tables / FineIndex), or null if unknown. */
  classify(guid: string): AssetKind | null;
}

export class ResourceManager {
  private readonly _refs = new Map<string, number>();
  /** Remembered kind per tracked GUID (so dispose works even if classify becomes unavailable). */
  private readonly _kinds = new Map<string, AssetKind>();
  private readonly _pinned = new Set<string>();

  constructor(private readonly _backends: ResourceBackends) {}

  /**
   * Increment refcounts for `guids` and load (via the backend) any that transition 0→1.
   * Loads run in parallel; individual failures are logged, not thrown (a missing asset must
   * not abort a whole level load — the lazy resolver is the safety net).
   */
  async acquire(guids: Iterable<string>): Promise<void> {
    const toLoad: Array<{ guid: string; kind: AssetKind }> = [];
    for (const guid of guids) {
      if (!guid) continue;
      const cur = this._refs.get(guid) ?? 0;
      this._refs.set(guid, cur + 1);
      if (cur === 0) {
        const kind = this._backends.classify(guid);
        if (kind) {
          this._kinds.set(guid, kind);
          toLoad.push({ guid, kind });
        }
      }
    }
    await Promise.all(
      toLoad.map(({ guid, kind }) =>
        Promise.resolve(this._backends.load(kind, guid)).catch((e) => {
          console.warn(`[ResourceManager] load failed for ${kind} ${guid}:`, e);
        }),
      ),
    );
  }

  /**
   * Decrement refcounts for `guids` and dispose (via the backend) any that transition →0,
   * unless the asset is pinned.
   */
  release(guids: Iterable<string>): void {
    for (const guid of guids) {
      if (!guid) continue;
      const cur = this._refs.get(guid) ?? 0;
      if (cur <= 0) continue;
      const next = cur - 1;
      if (next > 0) {
        this._refs.set(guid, next);
        continue;
      }
      this._refs.delete(guid);
      if (this._pinned.has(guid)) {
        // Keep the remembered kind; a pinned asset may be unpinned later and disposed then.
        continue;
      }
      this._disposeNow(guid);
    }
  }

  /** Mark assets as refcount-immune (never disposed at 0 while pinned). For `dontDestroyOnLoad`. */
  pin(guids: Iterable<string>): void {
    for (const g of guids) if (g) this._pinned.add(g);
  }

  /** Remove the pin; if the asset is already at refcount 0, dispose it now. */
  unpin(guids: Iterable<string>): void {
    for (const g of guids) {
      if (!g) continue;
      this._pinned.delete(g);
      if ((this._refs.get(g) ?? 0) === 0) this._disposeNow(g);
    }
  }

  /** Current refcount for an asset (0 if untracked). */
  refCount(guid: string): number {
    return this._refs.get(guid) ?? 0;
  }

  isPinned(guid: string): boolean {
    return this._pinned.has(guid);
  }

  /** Number of distinct assets currently tracked (refcount ≥ 1). */
  get trackedCount(): number {
    return this._refs.size;
  }

  /** Dispose everything currently tracked (e.g. on host shutdown), pinned included. */
  clear(): void {
    for (const [guid, kind] of this._kinds) {
      try {
        this._backends.dispose(kind, guid);
      } catch (e) {
        console.warn(`[ResourceManager] dispose failed for ${kind} ${guid}:`, e);
      }
    }
    this._refs.clear();
    this._kinds.clear();
    this._pinned.clear();
  }

  private _disposeNow(guid: string): void {
    const kind = this._kinds.get(guid) ?? this._backends.classify(guid);
    this._kinds.delete(guid);
    if (!kind) return;
    try {
      this._backends.dispose(kind, guid);
    } catch (e) {
      console.warn(`[ResourceManager] dispose failed for ${kind} ${guid}:`, e);
    }
  }
}
